import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useState} from 'react';
import styles from './sliding_wizard.module.scss';
import {AltButton} from '../button/alt_button.tsx';
import altButtonStyles from '../button/alt_button.module.scss';
import {ButtonColorType} from '../../enums/button_color_type.ts';

// import Swiper core and required modules
import type {Swiper as SwiperType} from 'swiper';
import {Swiper, SwiperSlide, useSwiper} from 'swiper/react';
import {A11y, Controller, EffectCreative, EffectFade} from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/effect-creative';
import {classNames} from '../../support/classnames.ts';

export interface Slide {
    title: React.ReactNode;
    content: React.ReactNode;
    footer: React.ReactNode;
    onPreviousClick: null | (() => void);
    clearBackground?: boolean;
    disableNextSlide?: boolean;
}

interface OwnProps {
    slides: Slide[];
    isEditing: boolean;
}

export interface SlidingWizardHandle {
    slideNext: () => void;
    slidePrev: () => void;
}

function SwiperBullets(props: OwnProps) {
    const swiper = useSwiper();

    const [realIndex, setRealIndex] = useState(swiper.realIndex);
    const onSwiperSlideChange = useCallback((s: SwiperType) => {
        setRealIndex(s.realIndex);
    }, []);
    useEffect(() => {
        swiper.on('slideChange', onSwiperSlideChange);
        return () => swiper.off('slideChange', onSwiperSlideChange);
    }, [swiper, onSwiperSlideChange]);

    const onPreviousCallback = props.slides[realIndex].onPreviousClick;

    return (
        <div className={styles.bullets_container}>
            {onPreviousCallback !== null && (
                <div className={styles.flex_grow}>
                    <AltButton
                        className={classNames(altButtonStyles.icon, altButtonStyles.hide_sm)}
                        onClick={() => onPreviousCallback()}
                        type="button"
                        colorType={ButtonColorType.Black}
                    >
                        <i className="fa-light fa-chevron-left" />
                    </AltButton>
                </div>
            )}
            <ol className={styles.swiper_bullets}>
                {props.slides.map((_, index) => (
                    <li
                        key={`swiper_bullet_${index}`}
                        className={realIndex > index ? styles.previous : realIndex === index ? styles.active : ''}
                    />
                ))}
            </ol>
            {onPreviousCallback !== null && <span className={styles.flex_grow} />}
        </div>
    );
}

export const SlidingWizard = forwardRef<SlidingWizardHandle, OwnProps>(function SlidingWizard(props, ref) {
    const [titleController, setTitleController] = useState<SwiperType | null>(null);
    const [mainController, setMainController] = useState<SwiperType | null>(null);
    const [footerController, setFooterController] = useState<SwiperType | null>(null);
    const scrollTop = () => window.scrollTo({top: 0, behavior: 'smooth'});

    useImperativeHandle(
        ref,
        () => {
            return {
                slideNext: () => {
                    mainController?.slideNext();
                    scrollTop();
                },
                slidePrev: () => {
                    mainController?.slidePrev();
                    scrollTop();
                },
            };
        },
        [mainController],
    );

    const onSwiperSlideChange = useCallback(
        (s: SwiperType) => {
            if (mainController && mainController.destroyed !== true) {
                const slide = props.slides[s.realIndex];
                mainController.allowSlideNext = slide.disableNextSlide !== true;
            }
        },
        [mainController],
    );
    useEffect(() => {
        if (mainController?.destroyed !== true) {
            if (props.isEditing) {
                mainController?.disable();
            } else {
                mainController?.enable();
            }
        }
        mainController?.on('slideChangeTransitionEnd', onSwiperSlideChange);
        return () => mainController?.off('slideChangeTransitionEnd', onSwiperSlideChange);
    }, [mainController, props.isEditing, onSwiperSlideChange]);

    return (
        <div className={styles.sliding_wizard}>
            <div className={styles.title_slider}>
                <Swiper
                    onSwiper={setTitleController}
                    modules={[Controller, EffectFade, A11y]}
                    effect="fade"
                    fadeEffect={{crossFade: true}}
                    slidesPerView={1}
                    allowTouchMove={false}
                >
                    {props.slides.map((slide, index) => (
                        <SwiperSlide className={styles.title_slide} key={`slider_title_${index}`}>
                            {slide.title}
                        </SwiperSlide>
                    ))}
                    <SwiperBullets {...props} />
                </Swiper>
            </div>

            <div className={styles.content_slider}>
                <div className={styles.content_container}>
                    <Swiper
                        onSwiper={setMainController}
                        controller={{control: [titleController!, footerController!]}}
                        modules={[Controller, EffectCreative, A11y]}
                        spaceBetween={250}
                        slidesPerView={1}
                        resistanceRatio={0}
                        preventInteractionOnTransition={true}
                        followFinger={false}
                        effect="creative"
                        creativeEffect={{
                            limitProgress: 1,
                            prev: {
                                shadow: false,
                                translate: [0, 0, -400],
                                opacity: 0,
                            },
                            next: {
                                shadow: false,
                                translate: ['100%', 0, 0],
                            },
                        }}
                    >
                        {props.slides.map((slide, index) => (
                            <SwiperSlide
                                className={`${styles.content_slide} ${slide.clearBackground ? styles.content_slide_clear : ''}`}
                                key={`slider_content_${index}`}
                            >
                                <div className={styles.content_slide_content}>{slide.content}</div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>

            <div className={styles.footer_slider}>
                <Swiper
                    onSwiper={setFooterController}
                    modules={[Controller, EffectFade, A11y]}
                    effect="fade"
                    slidesPerView={1}
                    allowTouchMove={false}
                >
                    {props.slides.map((slide, index) => (
                        <SwiperSlide className={styles.footer_slide} key={`slider_footer_${index}`}>
                            {slide.footer}
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </div>
    );
});
